import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Chip,
  IconButton,
  Autocomplete,
  TextField,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import AddAccount from '../accounts/AddAccount';

// Backend'den gelen cari bilgisi
interface ParsedAccountInfo {
  companyName: string;
  taxNumber: string;
  taxOffice?: string;
  address?: string;
  city?: string;
  district?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
}

// Backend'den gelen eşleştirilmiş cari bilgisi
interface MatchedAccountInfo {
  parsedAccount: ParsedAccountInfo;
  matchedAccountId: string | null;
  matchedAccountName: string | null;
  matchConfidence: number;
  isNewAccount: boolean;
  matchDescription: string;
}

// Cari hesap listesi için (PurchaseInvoice.tsx ile aynı)
interface Account {
  id: string;
  companyName: string;
  authorizedPerson: string;
  taxNumber: string;
  taxOffice: string;
  address: string;
  city: string;
  district: string;
  phone: string;
  email: string;
}

interface EInvoiceAccountMatchModalProps {
  open: boolean;
  onClose: () => void;
  onAccountSelected: (account: Account) => void;
  companyId: string;
  supplierInfo: MatchedAccountInfo | null;
}

const EInvoiceAccountMatchModal: React.FC<EInvoiceAccountMatchModalProps> = ({
  open,
  onClose,
  onAccountSelected,
  companyId,
  supplierInfo
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  
  // Yeni cari ekleme modalı için
  const [addAccountModalOpen, setAddAccountModalOpen] = useState<boolean>(false);
  const [prefillAccountData, setPrefillAccountData] = useState<any>(null);

  // Modal açıldığında cari hesapları yükle
  useEffect(() => {
    if (open && companyId) {
      loadAccounts();
    } else if (!open) {
      // Modal kapandığında seçimi temizle
      setSelectedAccount(null);
    }
  }, [open, companyId]);

  // 🚀 YENİ: supplierInfo yüklendikten sonra otomatik eşleştirme yap
  useEffect(() => {
    if (supplierInfo?.parsedAccount && accounts.length > 0 && !selectedAccount) {
      console.log('🔄 [ACCOUNT MATCH] supplierInfo yüklendi, otomatik eşleştirme yapılıyor...');
      
      const { taxNumber, companyName } = supplierInfo.parsedAccount;
      let matched = false;
      
      // Önce VKN ile dene
      if (taxNumber && !matched) {
        const cleanTaxNumber = taxNumber.trim();
        const matchedAccounts = accounts.filter((acc: Account) => {
          const accTaxNumber = acc.taxNumber?.trim() || '';
          return accTaxNumber === cleanTaxNumber || accTaxNumber.includes(cleanTaxNumber);
        });
        
        if (matchedAccounts.length > 0) {
          setSelectedAccount(matchedAccounts[0]);
          console.log('✅ [ACCOUNT MATCH] VKN ile otomatik eşleşme bulundu:', matchedAccounts[0].companyName);
          matched = true;
        }
      }
      
      // VKN ile bulunamadıysa firma adı ile dene
      if (companyName && !matched) {
        const cleanCompanyName = companyName.trim().toLowerCase();
        const matchedByName = accounts.filter((acc: Account) => {
          const accName = acc.companyName?.trim().toLowerCase() || '';
          return accName.includes(cleanCompanyName) || cleanCompanyName.includes(accName);
        });
        
        if (matchedByName.length > 0) {
          setSelectedAccount(matchedByName[0]);
          console.log('✅ [ACCOUNT MATCH] Firma adı ile eşleşme bulundu:', matchedByName[0].companyName);
          matched = true;
        }
      }
      
      if (!matched) {
        console.log('⚠️ [ACCOUNT MATCH] Otomatik eşleşme bulunamadı, manuel seçim gerekli');
      }
    }
  }, [supplierInfo, accounts]);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 [ACCOUNT MATCH] Cari hesaplar yükleniyor...', companyId);

      const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
      const result = response.data;
      const accountsList = result.data || result || [];
      
      setAccounts(accountsList);
      console.log(`✅ [ACCOUNT MATCH] ${accountsList.length} cari hesap yüklendi`);
    } catch (err: any) {
      console.error('❌ [ACCOUNT MATCH] Cari hesaplar yüklenirken hata:', err);
      setError(err.message || 'Cari hesaplar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // VKN ile otomatik arama (eski versiyon - artık loadAccounts içinde yapılıyor)
  const handleSearchByTaxNumber = async (taxNumber: string) => {
    if (!taxNumber || taxNumber.length < 10) return;

    try {
      setSearchLoading(true);
      
      // VKN'ye göre filtrele (tam eşleşme veya içerme)
      const matchedAccounts = accounts.filter(acc => {
        const accTaxNumber = acc.taxNumber?.trim() || '';
        const searchTaxNumber = taxNumber.trim();
        return accTaxNumber === searchTaxNumber || accTaxNumber.includes(searchTaxNumber);
      });

      if (matchedAccounts.length > 0) {
        setSelectedAccount(matchedAccounts[0]);
        console.log('✅ [ACCOUNT MATCH] VKN ile eşleşme bulundu:', matchedAccounts[0].companyName);
      } else if (supplierInfo?.parsedAccount?.companyName) {
        // VKN ile bulunamadıysa firma adı ile dene
        const companyName = supplierInfo.parsedAccount.companyName.trim().toLowerCase();
        const matchedByName = accounts.filter(acc => {
          const accName = acc.companyName?.trim().toLowerCase() || '';
          return accName.includes(companyName) || companyName.includes(accName);
        });
        
        if (matchedByName.length > 0) {
          setSelectedAccount(matchedByName[0]);
          console.log('✅ [ACCOUNT MATCH] Firma adı ile eşleşme bulundu:', matchedByName[0].companyName);
        }
      }
    } catch (err) {
      console.error('❌ [ACCOUNT MATCH] VKN araması hatası:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  // Manuel cari arama
  const handleSearchAccount = (searchText: string) => {
    setSearchTerm(searchText);
    // Autocomplete otomatik filtreleme yapacak
  };

  // Yeni cari ekleme modalını aç
  const handleOpenAddAccountModal = () => {
    // E-faturadan gelen bilgileri pre-fill için hazırla (varsa)
    if (supplierInfo?.parsedAccount) {
      const { parsedAccount } = supplierInfo;
      setPrefillAccountData({
        companyName: parsedAccount.companyName,
        taxNumber: parsedAccount.taxNumber,
        taxOffice: parsedAccount.taxOffice || '',
        phone: parsedAccount.phone || '',
        email: parsedAccount.email || '',
        website: parsedAccount.website || '',
        country: parsedAccount.country || 'Türkiye',
        city: parsedAccount.city || '',
        district: parsedAccount.district || '',
        address: parsedAccount.address || '',
      });
      console.log('✅ [ACCOUNT MATCH] E-fatura bilgileri ile form pre-fill ediliyor');
    } else {
      setPrefillAccountData(null);
      console.log('⚠️ [ACCOUNT MATCH] E-fatura bilgisi yok, boş form açılacak');
    }
    
    setAddAccountModalOpen(true);
  };

  // Yeni cari ekleme modalını kapat
  const handleCloseAddAccountModal = () => {
    setAddAccountModalOpen(false);
    setPrefillAccountData(null);
  };

  // Yeni cari başarıyla eklendikten sonra
  const handleAccountAdded = async () => {
    console.log('✅ [ACCOUNT MATCH] Yeni cari eklendi, liste yenileniyor...');
    
    // Modal'ı kapat
    handleCloseAddAccountModal();
    
    // Cari listesini yeniden yükle (otomatik eşleştirme içinde yapılacak)
    await loadAccounts();
  };

  const handleConfirm = () => {
    if (!selectedAccount) {
      setError('Lütfen bir cari hesap seçin veya yeni cari ekleyin');
      return;
    }

    console.log('✅ [ACCOUNT MATCH] Cari seçildi:', selectedAccount.companyName);
    onAccountSelected(selectedAccount);
    onClose();
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedAccount(null);
      setSearchTerm('');
      setError(null);
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={loading}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon color="primary" />
              <Typography variant="h6">Tedarikçi Cari Eşleştirme</Typography>
            </Box>
            {!loading && (
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <CircularProgress size={64} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Cari hesaplar yükleniyor...
              </Typography>
            </Box>
          ) : (
            <>
              {/* E-Faturadan Gelen Tedarikçi Bilgileri */}
              {supplierInfo && supplierInfo.parsedAccount ? (
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200', mb: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <BusinessIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                    E-Faturadan Gelen Tedarikçi Bilgileri
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <BusinessIcon fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight="bold">
                        {supplierInfo?.parsedAccount?.companyName || 'Bilinmeyen Firma'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>VKN:</strong> {supplierInfo?.parsedAccount?.taxNumber || 'N/A'}
                    </Typography>
                  </Grid>
                  
                  {supplierInfo?.parsedAccount?.taxOffice && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Vergi Dairesi:</strong> {supplierInfo.parsedAccount.taxOffice}
                      </Typography>
                    </Grid>
                  )}
                  
                  {supplierInfo?.parsedAccount?.phone && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {supplierInfo.parsedAccount.phone}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {supplierInfo?.parsedAccount?.email && (
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <EmailIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {supplierInfo.parsedAccount.email}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  
                  {(supplierInfo?.parsedAccount?.address || supplierInfo?.parsedAccount?.city) && (
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="flex-start" gap={0.5}>
                        <LocationOnIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {supplierInfo.parsedAccount.address && `${supplierInfo.parsedAccount.address}, `}
                          {supplierInfo.parsedAccount.district && `${supplierInfo.parsedAccount.district}/`}
                          {supplierInfo.parsedAccount.city}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>
              ) : (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    E-faturada tedarikçi bilgisi bulunamadı. Lütfen manuel olarak cari hesap seçin veya yeni cari ekleyin.
                  </Typography>
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Cari Hesap Seçimi */}
              <Box>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Cari Hesap Seçin veya Yeni Ekleyin
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                  <Autocomplete
                    fullWidth
                    size="medium"
                    options={accounts}
                    getOptionLabel={(option) => `${option.companyName} (${option.taxNumber})`}
                    value={selectedAccount}
                    onChange={(_, newValue) => {
                      setSelectedAccount(newValue);
                      setError(null);
                    }}
                    onInputChange={(_, value) => handleSearchAccount(value)}
                    loading={searchLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Cari Hesap Ara"
                        variant="outlined"
                        placeholder="Firma adı veya VKN ile arayın..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {option.companyName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            VKN: {option.taxNumber} | {option.city || 'Şehir belirtilmemiş'}
                          </Typography>
                        </Box>
                      </li>
                    )}
                  />
                  
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    startIcon={<AddCircleIcon />}
                    onClick={handleOpenAddAccountModal}
                    sx={{ minWidth: '160px', height: '56px' }}
                  >
                    Yeni Cari Ekle
                  </Button>
                </Box>

                {/* Seçili Cari Bilgileri */}
                {selectedAccount && (
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', mt: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <CheckCircleIcon color="success" />
                      <Typography variant="subtitle2" fontWeight="bold" color="success.dark">
                        Seçili Cari Hesap
                      </Typography>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Typography variant="body2" fontWeight="bold">
                          {selectedAccount.companyName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          <strong>VKN:</strong> {selectedAccount.taxNumber}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Vergi Dairesi:</strong> {selectedAccount.taxOffice || 'Belirtilmemiş'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} variant="outlined" disabled={loading}>
            İptal
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            disabled={!selectedAccount || loading}
            startIcon={<CheckCircleIcon />}
          >
            Cariyi Onayla ve Devam Et
          </Button>
        </DialogActions>
      </Dialog>

      {/* Yeni Cari Ekleme Modalı */}
      {addAccountModalOpen && (
        <AddAccount
          open={addAccountModalOpen}
          onClose={handleAccountAdded}
          prefillData={prefillAccountData}
        />
      )}
    </>
  );
};

export default EInvoiceAccountMatchModal;
